import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class AnalyticsSettingsComponent extends Component {
  static readonly TYPE_ID: string = 'f21ba80ed75b40dabb4d366fc23bafe5';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly facebookPixelId: string,
    public readonly tikTokPixelId: string,
    public readonly googleTagManagerId: string,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return AnalyticsSettingsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, AnalyticsSettingsComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): AnalyticsSettingsComponent {
    return new AnalyticsSettingsComponent(
        MapUtils.get<string>(map, 'facebookPixelId'),
        MapUtils.get<string>(map, 'tikTokPixelId'),
        MapUtils.get<string>(map, 'googleTagManagerId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'facebookPixelId': this.facebookPixelId,
      'tikTokPixelId': this.tikTokPixelId,
      'googleTagManagerId': this.googleTagManagerId,
    };
  }

  clone(): this {
    return new AnalyticsSettingsComponent(
        this.facebookPixelId,
        this.tikTokPixelId,
        this.googleTagManagerId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.facebookPixelId == this.facebookPixelId &&
      other.tikTokPixelId == this.tikTokPixelId &&
      other.googleTagManagerId == this.googleTagManagerId;
  }
}

export class AnalyticsSettingsComponentBuilder extends DataModelBuilder<AnalyticsSettingsComponent> {
  build(context: SerializationContext, map: SerializedData): AnalyticsSettingsComponent {
    return AnalyticsSettingsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(AnalyticsSettingsComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(AnalyticsSettingsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the following changes were made
    //  - `pixelId` parameter was renamed to `facebookPixelId`
    //  - `tikTokPixelId` parameter of type `string` was introduced
    if(version < 2) {
      map['facebookPixelId'] ??= MapUtils.get<string>(map, 'pixelId');
      map['tikTokPixelId'] ??= '';
      version = 2;
    }

    // In this version the following changes were made
    //  - `googleTagManagerId` parameter of type `string` was introduced
    if(version < 3) {
      map['googleTagManagerId'] ??= '';
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
